<template>
  <div :class="{ [$style.product]: true, [$style.productMini]: miniBasket }">
    <RouterLink
      v-if="miniBasket"
      :to="skuRoute"
      :class="$style.image"
      @click.native="$emit('close')"
    >
      <BasketProductImage :src="mediaUrl" :alt="name" :class="$style.image" />
    </RouterLink>
    <BasketProductImage v-if="!miniBasket" :src="mediaUrl" :alt="name" :class="$style.image" />
    <div :class="$style.info">
      <RouterLink
        v-if="miniBasket"
        :to="skuRoute"
        :class="$style.name"
        @click.native="$emit('close')"
      >
        <span>{{ name }}</span>
      </RouterLink>
      <span v-if="!miniBasket" :class="$style.name">{{ name }}</span>
      <dl v-if="!miniBasket" :class="$style['product-data']" class="dl-list">
        <dd>{{ $t('checkout.basket.prodcode') }}</dd>
        <dt>
          <RouterLink :to="skuRoute" @click.native="$emit('close')"
            ><b>{{ prodcode }}</b>
          </RouterLink>
        </dt>
        <dd v-show="options">{{ $t('checkout.basket.options') }}</dd>
        <dt v-show="options">
          <b>{{ options }}</b>
        </dt>
      </dl>
    </div>
    <dl v-if="miniBasket" :class="$style['product-data']" class="dl-list">
      <dd>{{ $t('checkout.basket.prodcode') }}</dd>
      <dt>
        <RouterLink :to="skuRoute" @click.native="$emit('close')"
          ><b>{{ prodcode }}</b>
        </RouterLink>
      </dt>
      <dd>{{ $t('checkout.basket.options') }}</dd>
      <dt>
        <b>{{ options }}</b>
      </dt>
    </dl>
    <span v-if="!miniBasket" :class="$style.quantityCheckout">{{ $t('product.quantity') }}: </span>
    <span :class="$style.quantity">{{ totalQuantity }}</span>
    <span v-if="!miniBasket" :class="$style.bdpLabel">BDP: </span>
    <BasketProductPrice
      v-if="!miniBasket"
      :price="price"
      :class="$style.bdp"
      :currency="currency"
    />
    <BasketProductPrice :price="totalSum" :class="$style.price" :currency="currency" />
    <BasketProductRemove
      v-show="allowedDelete"
      :class="$style.remove"
      :disabled="pending"
      @delete="removeProducts"
    />
    <v-btn
      color="primary"
      dense
      icon
      :class="$style.edit"
      :disabled="pending"
      @click="editProducts"
    >
      <IconSelect icon="edit" :height="24" :width="24" :class="$style.editIcon" />
    </v-btn>
    <BasketProductSplit
      :class="$style.productSplit"
      v-bind="splitData"
      :mini-basket="miniBasket"
      :is-mobile="isMobile"
      :pending="pending"
      @updateBasketItem="updateBasketItem"
    />
  </div>
</template>

<script>
/* global Bugsnag */
import { get, isEmpty, isUndefined, pick, mapKeys, mapValues } from 'lodash-es'
import { getISOWeek, getISOWeekYear, parseISO } from 'date-fns'
import { camelCase } from 'change-case'
import BASKET_MUTATIONS from '@/js/plugins/store/modules/aimeos/mutation-types/basket-mutation-types.js'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import AIMEOS_ACTIONS from '@/js/plugins/store/modules/aimeos/actions/actions-types.js'
import AIMEOS_GETTERS from '@/js/plugins/store/modules/aimeos/getter-types/index.js'
// import BIKE_ATTRIBUTES from '@/js/helper/bike-attributes.js'
import { REQUEST_STATE } from '@/js/plugins/store/modules/aimeos/constants'
import FILTER_CONSTANTS from '@/js/helper/filter-constants.js'

import BasketProductImage from '@/js/components/Basket/BasketProductImage.vue'
import BasketProductPrice from '@/js/components/Basket/BasketProductPrice.vue'
import BasketProductRemove from '@/js/components/Basket/BasketProductRemove.vue'
import BasketProductSplit from '@/js/components/Basket/BasketProductSplit.vue'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

// import { getContentBaseurl } from '@/js/helper/meta-helper.js'

// import ElementPropsMixin from '@/js/mixins/element-props-mixin.js'
import OrderBaseProductPropsMixin from '@/js/mixins/order-base-product-props-mixin.js'
import RequestOrderProductDataMixin from '@/js/mixins/request-order-product-data-mixin.js'
import ProductSkuLinkMixin from '@/js/mixins/product-sku-link-mixin.js'

import { includedCustomBasketAttributes } from '@/js/helper/basket-data-helper.js'

import Bugsnag from '@bugsnag/js'

const splitKeys = [
  'key',
  'quantity',
  'value',
  'week',
  'year',
  'dateFormatted',
  'selfUrl',
  'paymentTerms',
  'basketStatusIsOk',
  'basketStatus',
]

export default {
  name: 'BasketProductGrouped',
  components: {
    BasketProductImage,
    BasketProductPrice,
    BasketProductRemove,
    BasketProductSplit,
    IconSelect,
  },
  mixins: [OrderBaseProductPropsMixin, RequestOrderProductDataMixin, ProductSkuLinkMixin],
  props: {
    miniBasket: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    totalSum: {
      type: Number,
      default: 0,
    },
    totalQuantity: {
      type: Number,
      default: 0,
    },
    variants: {
      type: Object,
      default: () => ({}),
    },
    relatedIds: {
      type: Array,
      default: () => [],
    },
    selfBasketUrl: {
      type: String,
      default: undefined,
    },
    selfUrl: {
      type: String,
      default: undefined,
    },
    paymentTerms: {
      type: Array,
      default: () => [],
    },
    deliveryTerms: {
      type: Array,
      default: () => [],
    },
    batteryUpgradeChoice: {
      type: Array,
      default: undefined,
    },
    productOrderDelivery: {
      type: Object,
      default: undefined,
    },
    productOrderPaymentTarget: {
      type: Object,
      default: undefined,
    },
    customBasketAttributes: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      productDataPending: false,
    }
  },
  computed: {
    ...mapGetters(STOREMODULES.AIMEOS, {
      basketRequestCount: AIMEOS_GETTERS.BASKET_REQUEST_COUNT,
      basketPending: `BASKET_${REQUEST_STATE.STATE_PENDING}`,
    }),
    pending() {
      return this.productDataPending || this.basketPending
    },
    splitData() {
      return {
        customBasketAttributes: mapValues(
          pick(this.customBasketAttributes, includedCustomBasketAttributes),
          'value',
        ),
        amountAsap: this.quantity,
        selfUrl: this.selfUrl,
        productId: this.productId,
        paymentTerms: this.paymentTerms,
        deliveryTerms: this.transformDeliveryTerms(this.deliveryTerms),
        basketStatusIsOk: this.basketStatusIsOk,
        basketStatus: this.basketStatus,
        batteryUpgradeChoice: this.batteryUpgradeChoice,
        productOrderDate: this.productOrderDate.map(i => {
          return {
            ...pick(i, splitKeys),
            deliveryTerms: this.transformDeliveryTerms(get(i, 'deliveryTerms', [])),
            customBasketAttributes: mapValues(get(i, 'customBasketAttributes', {}), 'value'),
          }
        }),
        productOrderCw: this.productOrderCw.map(i => {
          return {
            ...pick(i, splitKeys),
            deliveryTerms: this.transformDeliveryTerms(get(i, 'deliveryTerms', [])),
            customBasketAttributes: mapValues(get(i, 'customBasketAttributes', {}), 'value'),
          }
        }),
      }
    },
    productOrderDate() {
      return Object.values(get(this.variants, 'productOrderDate', {})).map(item => {
        const key = get(item, ['productOrderDate', 'value'])
        const dateFormatted = key ? this.$d(parseISO(key)) : '?'
        return {
          ...item,
          dateFormatted,
          key,
        }
      })
    },
    productOrderCw() {
      return Object.values(get(this.variants, 'productOrderCw', {})).map(item => {
        const key = get(item, ['productOrderCw', 'value'])
        const date = key ? parseISO(key) : undefined
        const dateFormatted = date ? this.$d(date) : undefined
        const week = date ? `${getISOWeek(date)}`.padStart(2, '0') : undefined
        const year = date ? `${getISOWeekYear(date)}`.substring(2) : undefined
        return {
          ...item,
          date,
          dateFormatted,
          week,
          year,
          key,
        }
      })
    },
    resolvedBasketAttributes() {
      return mapKeys(
        get(this.requestedBasketAttributes, [this.productId, 'basketAttributes'], {}),
        (v, k) => camelCase(k),
      )
    },
    // productOrderPaymentTargetAttribute() {
    //   return get(this.resolvedBasketAttributes, BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_PAYMENT_TARGET)
    // },
    // productOrderOrderDelivberyAttribute() {
    //   return get(this.resolvedBasketAttributes, BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_DELIVERY)
    // },
    // image() {
    //   return `${getContentBaseurl(this.meta)}/${this.mediaUrl}`
    // },
    code() {
      return this.prodcode
    },
    batteryUpgrade() {
      const batteryUpgrade = get(this.batteryUpgradeChoice, 1)
      return batteryUpgrade
        ? `${this.$t('term.battery')} ${batteryUpgrade} ${this.$t('term.wh')}`
        : undefined
    },
    options() {
      return this.batteryUpgrade ? this.batteryUpgrade : undefined
    },
  },
  watch: {
    productDataPending() {
      this.$emit('dataFetchPending', this.productDataPending)
    },
  },
  // created() {
  //   console.log({ myCode: this.code })
  // },

  methods: {
    ...mapActions(STOREMODULES.AIMEOS, [AIMEOS_ACTIONS.BASKET_REQUEST]),
    ...mapMutations(STOREMODULES.AIMEOS, {
      addBasketMessage: BASKET_MUTATIONS.ADD_BASKET_MESSAGE,
    }),

    // transformCustomBasketAttributes(attributes) {
    //   return Object.entries(attributes).reduce((acc, [k, v]) => {
    //     acc[k] = v.value
    //     return acc
    //   }, {})
    // },

    // checkout.delivery.types.default
    // checkout.delivery.types.express
    // checkout.delivery.mini.types.express
    // checkout.delivery.mini.types.default
    transformDeliveryTerms(terms) {
      return terms.map(code => ({
        code,
        text: this.$t(`checkout.delivery.types.${code === '' ? 'default' : code}`),
      }))
    },
    removeProducts() {
      if (!this.selfBasketUrl) {
        return
      }

      this.$emit('has-deleted-from-basket', true)

      this[AIMEOS_ACTIONS.BASKET_REQUEST]({
        method: 'DELETE',
        url: this.selfBasketUrl,
        data: {
          data: this.relatedIds.map(id => ({
            id,
          })),
        },
      })
    },
    async editProducts() {
      this.productDataPending = true
      if (isEmpty(this.requestedBasketAttributes)) {
        await this.requestProductData()
      }
      await this.requestProductData()
      this.$emit('openBasketForm', {
        basketAttributes: get(
          this.requestedBasketAttributes,
          [this.productId, 'basketAttributes'],
          {},
        ),
        articleType: get(this.requestedBasketAttributes, [this.productId, 'ArticleType']),
        productId: this.productId,
        code: this.prodcode,
        name: this.name,
        productScope: this.getProductScope(this.productId, this.requestedBasketAttributes),
        basketValues: this.splitData,
        basketLinkUrl: this.selfBasketUrl,
        relatedIds: this.relatedIds,
        groupPrices: get(this.requestedBasketAttributes, [this.productId, 'groupPrices'], {}),
        hasSoldOutSelling: get(
          this.requestedBasketAttributes,
          [this.productId, 'hasSoldOutSelling'],
          {},
        ),
        upgradeAttributes: get(this.requestedBasketAttributes, [
          this.productId,
          'upgradeAttributes',
        ]),
      })
      this.productDataPending = false
    },
    getProductScope(productId, requestedBasketAttributes) {
      const articleType = get(requestedBasketAttributes, [productId, 'ArticleType'])
      return articleType === FILTER_CONSTANTS.ARTICLETYPE_CODE.PART
        ? FILTER_CONSTANTS.SCOPE.SERVICE
        : articleType === FILTER_CONSTANTS.ARTICLETYPE_CODE.ACCESSORY
        ? FILTER_CONSTANTS.SCOPE.ACCESSORIES
        : FILTER_CONSTANTS.SCOPE.BIKES
    },
    async updateBasketItem({
      // productId,
      type,
      value,
      quantity,
      selfUrl,
      customBasketAttributes,
    } = {}) {
      this.productDataPending = true
      if (isEmpty(this.requestedBasketAttributes)) {
        await this.requestProductData()
      }

      if (isEmpty(this.resolvedBasketAttributes)) {
        const message = `No Basketattributes for ${this.productId},. Deleting product in basket.`
        console.log(message)
        Bugsnag.notify(message)
      }

      const attributeId = get(this.resolvedBasketAttributes, camelCase(type))
      const deleteOnly = isUndefined(attributeId)
      if (deleteOnly) {
        console.log('BasketAttributeId not found')
      }

      const custom = Object.entries(customBasketAttributes).reduce(
        (acc, [k, v]) => {
          const id = get(this.resolvedBasketAttributes, k)
          if (
            !isUndefined(id) &&
            !isUndefined(v) &&
            v !== '' &&
            id !== attributeId // Don’t overwrite changed value
          ) {
            acc[id] = v
          }
          return acc
        },
        !isUndefined(value) && value !== '' ? { [attributeId]: value } : {},
      )

      this.productDataPending = false
      await this[AIMEOS_ACTIONS.BASKET_REQUEST]({
        method: 'DELETE',
        url: selfUrl,
      })

      if (deleteOnly) {
        this.addBasketMessage({
          message: this.$t('checkout.basket.message.removedProduct', {
            name: this.name,
            code: this.prodcode,
          }),
        })
        return
      }

      const data = {
        data: [
          this.getBasketProductEntry({
            quantity: parseInt(quantity, 10),
            custom,
          }),
        ],
      }

      await this[AIMEOS_ACTIONS.BASKET_REQUEST]({
        method: 'POST',
        url: this.selfBasketUrl,
        data,
      })
    },
  },
}
</script>

<style lang="scss" module>
.product {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    grid-column-gap: 0.5rem;

    grid-template-areas:
      'image info quantity edit bdp price remove'
      'split split split split split split split';
    grid-template-columns: 6rem minmax(6rem, 100%) 3rem 3rem 8rem 8rem 3rem;
  }
  display: grid;
  grid-row-gap: 0.35rem;
  grid-column-gap: 0.2rem;

  grid-template-areas:
    'image info info info info info'
    'quantityCheckout quantity quantity edit price remove'
    '. . . bdpLabel bdp .'
    'split split split split split split';

  grid-template-rows: auto;
  grid-template-columns: 6rem minmax(1rem, 1fr) 1fr 3fr 3fr 1fr;

  align-items: center;
  padding: 0.75rem 0.25rem;

  border-top: var(--border-grey-lighten-1);
}

.productMini {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    grid-template-areas:
      'image info info info info info'
      'image code quantity edit price remove'
      'split split split split split split';
    grid-template-rows: auto;
    grid-template-columns: 6rem minmax(6rem, 100%) 2.5rem 2rem 8rem 2rem;
  }
  grid-template-areas:
    'image info info info info info'
    'image code code code code code'
    '. quantity edit price price remove'
    'split split split split split split';
  grid-template-rows: auto;
  grid-template-columns: 6rem minmax(1rem, 1fr) 1fr 3fr 3fr 1fr;

  .edit {
    // grid-area: quantity;

    justify-self: end;
  }

  // .quantity {
  //   margin-right: 2rem;
  // }

  .product-data {
    grid-area: code;
  }
}

.product-data {
  font-size: 0.75rem;
  font-family: var(--font-mono);
  line-height: 0.875rem;

  b {
    color: rgba(0, 0, 0, 0.6);
  }

  &:global(.dl-list) {
    gap: 0 0.75rem;
    align-items: center;
  }
}

.productSplit {
  grid-area: split;
}

.name {
  span {
    color: rgba(0, 0, 0, 0.6);
  }
  // grid-area: name;
}

.image {
  grid-area: image;
}

.info {
  display: flex;
  flex-direction: column;
  grid-area: info;
}

.bdp {
  grid-area: bdp;
}

.bdpLabel {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: none;
  }
  grid-area: bdpLabel;

  font-weight: bold;
}

.price {
  grid-area: price;
}

.quantity {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    font-size: inherit;
    text-align: right;
  }
  grid-area: quantity;

  font-size: 0.875rem;

  text-align: left;
}

.quantityCheckout {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: none;
  }
  grid-area: quantityCheckout;
  padding-left: 0.5rem;

  font-weight: bold;
  font-size: 0.875rem;
}

.edit,
.remove {
  min-width: 2rem;
  max-width: 2rem;
  max-height: 2rem;
}

.remove {
  grid-area: remove;
}

.edit {
  grid-area: edit;
}

.editIcon {
  width: 24px;
  height: 24px;
}
</style>
